import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  EmpresaPortal,
  RelatorioFamilyData,
  RelatorioFamilyFilters,
  getEmpresasNotif,
  previewRelatorioFamily,
  sendRelatorioFamily,
} from "src/apis/eezyfamily";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { getFirstDayOfMonth, getToday } from "src/utils/date";

const ROWS = 10;

const METHOD_LABELS: Record<string, string> = {
  card: "Cartão",
  boleto: "Boleto",
  pix: "Pix",
  unknown: "Desconhecido",
};

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);

const toIso = (date: string, endOfDay: boolean, tz: string): string => {
  if (!date) return "";
  const time = endOfDay ? "T23:59:59" : "T00:00:00";
  return `${date}${time}${tz}`;
};

const buildCsv = (data: RelatorioFamilyData): string => {
  const lines: string[][] = [];
  const t = data.current.totals;

  lines.push(["=== TOTAIS ==="]);
  lines.push(["Métrica", "Valor"]);
  lines.push(["Sessões Pagas", String(t.paid_count)]);
  lines.push(["Receita Total (R$)", String((t.paid_sum_cents / 100).toFixed(2))]);
  lines.push(["Sessões Inadimplentes", String(t.unpaid_count)]);
  lines.push(["Valor Inadimplente (R$)", String((t.unpaid_sum_cents / 100).toFixed(2))]);
  lines.push([]);

  lines.push(["=== POR MÉTODO DE PAGAMENTO ==="]);
  lines.push(["Método", "Qtde Sessões", "Valor Total (R$)"]);
  data.current.by_payment_method.forEach((m) => {
    lines.push([
      METHOD_LABELS[m.method] ?? m.method,
      String(m.count),
      String((m.total_cents / 100).toFixed(2)),
    ]);
  });
  lines.push([]);

  lines.push(["=== POR EMPRESA (PAGAS) ==="]);
  lines.push(["Empresa", "Qtde Sessões", "Valor Total (R$)", "Colaboradores Pagantes"]);
  data.current.by_company.forEach((c) => {
    lines.push([
      `"${c.company}"`,
      String(c.count),
      String((c.total_cents / 100).toFixed(2)),
      String(c.paying_collaborators),
    ]);
  });
  lines.push([]);

  lines.push(["=== INADIMPLÊNCIA POR EMPRESA ==="]);
  lines.push(["Empresa", "Sessões Inadimplentes", "Valor (R$)", "Taxa Inadimplência (%)"]);
  data.current.delinquency_by_company.forEach((d) => {
    lines.push([
      `"${d.company}"`,
      String(d.unpaid_count),
      String((d.unpaid_sum_cents / 100).toFixed(2)),
      String((d.delinquency_rate * 100).toFixed(1)),
    ]);
  });

  return lines.map((row) => row.join(",")).join("\n");
};

const triggerDownload = (content: string, filename: string): void => {
  const blob = new Blob(["﻿" + content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const deltaChip = (current: number, prev: number) => {
  if (!prev) return null;
  const pct = ((current - prev) / prev) * 100;
  const label = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs. período anterior`;
  return (
    <Chip
      size="small"
      label={label}
      color={pct >= 0 ? "success" : "error"}
      sx={{ ml: 1, fontSize: "0.7rem" }}
    />
  );
};

const EFRelatorioFinanceiroPage: FC = (): JSX.Element => {
  const today = getToday();
  const firstDay = getFirstDayOfMonth();

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today);
  const [tzOffset, setTzOffset] = useState("-03:00");
  const [companyId, setCompanyId] = useState("");
  const [locale, setLocale] = useState("pt-BR");
  const [empresas, setEmpresas] = useState<EmpresaPortal[]>([]);

  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [compStart, setCompStart] = useState("");
  const [compEnd, setCompEnd] = useState("");
  const [compTz, setCompTz] = useState("-03:00");

  const [previewData, setPreviewData] = useState<RelatorioFamilyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [byCompanyPage, setByCompanyPage] = useState(0);
  const [delinqPage, setDelinqPage] = useState(0);

  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [attachCsv, setAttachCsv] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState("");

  const fetchEmpresas = useCallback(async () => {
    try {
      const data = await getEmpresasNotif();
      setEmpresas(data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  // Auto-fill email subject when dates change
  useEffect(() => {
    if (startDate && endDate) {
      setEmailSubject(`Relatório Financeiro EezyFamily — ${startDate} a ${endDate}`);
    }
  }, [startDate, endDate]);

  const copyPreviousPeriod = () => {
    if (!startDate || !endDate) return;
    const start = new Date(startDate + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    const durationMs = end.getTime() - start.getTime();
    const prevEnd = new Date(start.getTime() - 86400000);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    setCompEnd(prevEnd.toISOString().split("T")[0]);
    setCompStart(prevStart.toISOString().split("T")[0]);
    setCompTz(tzOffset);
  };

  const buildFilters = (): RelatorioFamilyFilters => ({
    start: toIso(startDate, false, tzOffset),
    end: toIso(endDate, true, tzOffset),
    comp_start: comparisonEnabled && compStart ? toIso(compStart, false, compTz) : null,
    comp_end: comparisonEnabled && compEnd ? toIso(compEnd, true, compTz) : null,
    company_id: companyId ? Number(companyId) : null,
    locale,
  });

  const handlePreview = async () => {
    if (!startDate || !endDate) {
      setError("Selecione as datas de início e fim.");
      return;
    }
    setLoading(true);
    setError("");
    setPreviewData(null);
    setByCompanyPage(0);
    setDelinqPage(0);
    try {
      const filters = buildFilters();
      const data = await previewRelatorioFamily(filters);
      setPreviewData(data);
    } catch (err: any) {
      setError(err?.message ?? "Erro ao gerar prévia.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!previewData) return;
    const content = buildCsv(previewData);
    triggerDownload(
      content,
      `relatorio_financeiro_${startDate}_a_${endDate}.csv`
    );
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailTo.trim()) {
      setSendError("Informe pelo menos um destinatário.");
      return;
    }
    setSendLoading(true);
    setSendError("");
    setSendSuccess(false);
    try {
      const filters = buildFilters();
      await sendRelatorioFamily({
        ...filters,
        to: emailTo.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        cc: emailCc ? emailCc.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [],
        subject: emailSubject,
        attach_csv: attachCsv,
      });
      setSendSuccess(true);
    } catch (err: any) {
      setSendError(err?.message ?? "Erro ao enviar e-mail.");
    } finally {
      setSendLoading(false);
    }
  };

  const current = previewData?.current;
  const comparison = previewData?.comparison;

  const byCompanySlice = useMemo(
    () =>
      (current?.by_company ?? []).slice(
        byCompanyPage * ROWS,
        byCompanyPage * ROWS + ROWS
      ),
    [current, byCompanyPage]
  );

  const delinqSlice = useMemo(
    () =>
      (current?.delinquency_by_company ?? []).slice(
        delinqPage * ROWS,
        delinqPage * ROWS + ROWS
      ),
    [current, delinqPage]
  );

  return (
    <PageContainer
      title="Relatório Financeiro EezyFamily"
      error={error}
      guard={isUserAdministrator}
    >
      {/* ─── Filtros ─────────────────────────────────────────────────────── */}
      <Typography variant="h6" gutterBottom>
        Filtros do Período
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormGroup>
            <FormLabel required>Data Início</FormLabel>
            <TextField
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormGroup>
            <FormLabel required>Data Fim</FormLabel>
            <TextField
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormGroup>
            <FormLabel>Fuso Horário</FormLabel>
            <TextField
              value={tzOffset}
              onChange={(e) => setTzOffset(e.target.value)}
              size="small"
              placeholder="-03:00"
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormGroup>
            <FormLabel>Empresa</FormLabel>
            <Select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value as string)}
              displayEmpty
              size="small"
            >
              <MenuItem value="">Todas</MenuItem>
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.trade_name}
                </MenuItem>
              ))}
            </Select>
          </FormGroup>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormGroup>
            <FormLabel>Locale</FormLabel>
            <Select
              value={locale}
              onChange={(e) => setLocale(e.target.value as string)}
              size="small"
            >
              <MenuItem value="pt-BR">pt-BR</MenuItem>
              <MenuItem value="en-US">en-US</MenuItem>
            </Select>
          </FormGroup>
        </Grid>
      </Grid>

      {/* ─── Período de Comparação ────────────────────────────────────────── */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FormControlLabel
            control={
              <Checkbox
                checked={comparisonEnabled}
                onChange={(e) => setComparisonEnabled(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label="Comparar com período anterior"
            onClick={(e) => e.stopPropagation()}
          />
        </AccordionSummary>
        {comparisonEnabled && (
          <AccordionDetails>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <FormGroup>
                <FormLabel>Data Início (comparação)</FormLabel>
                <TextField
                  type="date"
                  value={compStart}
                  onChange={(e) => setCompStart(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Data Fim (comparação)</FormLabel>
                <TextField
                  type="date"
                  value={compEnd}
                  onChange={(e) => setCompEnd(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Fuso</FormLabel>
                <TextField
                  value={compTz}
                  onChange={(e) => setCompTz(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                />
              </FormGroup>
              <Button
                variant="outlined"
                size="small"
                onClick={copyPreviousPeriod}
                sx={{ alignSelf: "flex-end", mb: "2px" }}
              >
                Copiar período anterior do mesmo tamanho
              </Button>
            </Box>
          </AccordionDetails>
        )}
      </Accordion>

      {/* ─── Botão Gerar Prévia ───────────────────────────────────────────── */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={handlePreview}
          disabled={loading || !startDate || !endDate}
          startIcon={loading ? <CircularProgress size="1rem" color="inherit" /> : undefined}
        >
          {loading ? "Gerando..." : "Gerar Prévia"}
        </Button>
      </Box>

      {/* ─── Dashboard ───────────────────────────────────────────────────── */}
      {current && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Resultado
          </Typography>

          {/* Totais */}
          <Grid container spacing={2}>
            {[
              {
                label: "Sessões Pagas",
                value: current.totals.paid_count,
                prev: comparison?.totals.paid_count,
                format: (v: number) => String(v),
              },
              {
                label: "Receita Total",
                value: current.totals.paid_sum_cents,
                prev: comparison?.totals.paid_sum_cents,
                format: formatCurrency,
              },
              {
                label: "Sessões Inadimplentes",
                value: current.totals.unpaid_count,
                prev: comparison?.totals.unpaid_count,
                format: (v: number) => String(v),
              },
              {
                label: "Valor Inadimplente",
                value: current.totals.unpaid_sum_cents,
                prev: comparison?.totals.unpaid_sum_cents,
                format: formatCurrency,
              },
            ].map(({ label, value, prev, format }) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {format(value)}
                  </Typography>
                  {prev !== undefined &&
                    deltaChip(value, prev)}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Por método de pagamento */}
          <Accordion sx={{ mt: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Por Método de Pagamento
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Método</strong></TableCell>
                      <TableCell align="right"><strong>Qtde Sessões</strong></TableCell>
                      <TableCell align="right"><strong>Valor Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {current.by_payment_method.map((m) => (
                      <TableRow key={m.method} hover>
                        <TableCell>{METHOD_LABELS[m.method] ?? m.method}</TableCell>
                        <TableCell align="right">{m.count}</TableCell>
                        <TableCell align="right">{formatCurrency(m.total_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* Por empresa — pagas */}
          <Accordion sx={{ mt: 1 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Por Empresa — Sessões Pagas
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Qtde Sessões</strong></TableCell>
                      <TableCell align="right"><strong>Valor Total</strong></TableCell>
                      <TableCell align="right"><strong>Colaboradores Pagantes</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byCompanySlice.map((c, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{c.company}</TableCell>
                        <TableCell align="right">{c.count}</TableCell>
                        <TableCell align="right">{formatCurrency(c.total_cents)}</TableCell>
                        <TableCell align="right">{c.paying_collaborators}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={current.by_company.length}
                page={byCompanyPage}
                onPageChange={(_, p) => setByCompanyPage(p)}
                rowsPerPage={ROWS}
                rowsPerPageOptions={[ROWS]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count}`
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Inadimplência */}
          <Accordion sx={{ mt: 1 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Inadimplência por Empresa
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Sessões Inadimplentes</strong></TableCell>
                      <TableCell align="right"><strong>Valor</strong></TableCell>
                      <TableCell align="right"><strong>Taxa Inadimplência</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {delinqSlice.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{d.company}</TableCell>
                        <TableCell align="right">{d.unpaid_count}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(d.unpaid_sum_cents)}
                        </TableCell>
                        <TableCell align="right">
                          {(d.delinquency_rate * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={current.delinquency_by_company.length}
                page={delinqPage}
                onPageChange={(_, p) => setDelinqPage(p)}
                rowsPerPage={ROWS}
                rowsPerPageOptions={[ROWS]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count}`
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* ─── Exportação ─────────────────────────────────────────────── */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Exportação
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Tooltip title={`Baixar relatório do período ${startDate} a ${endDate}`}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCsv}
              >
                Baixar CSV
              </Button>
            </Tooltip>
          </Box>

          {/* Envio por e-mail */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Enviar por E-mail
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="form"
                onSubmit={handleSendEmail}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                {sendError && (
                  <Alert severity="error">{sendError}</Alert>
                )}
                {sendSuccess && (
                  <Alert severity="success">
                    Relatório enviado com sucesso!
                  </Alert>
                )}
                <FormGroup>
                  <FormLabel required>Destinatários (Para)</FormLabel>
                  <TextField
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="email@exemplo.com, outro@exemplo.com"
                    size="small"
                    helperText="Separar múltiplos e-mails por vírgula ou ponto-e-vírgula"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>CC</FormLabel>
                  <TextField
                    value={emailCc}
                    onChange={(e) => setEmailCc(e.target.value)}
                    placeholder="cc@exemplo.com"
                    size="small"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Assunto</FormLabel>
                  <TextField
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    size="small"
                  />
                </FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attachCsv}
                      onChange={(e) => setAttachCsv(e.target.checked)}
                    />
                  }
                  label="Anexar CSV"
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      sendLoading ? (
                        <CircularProgress size="1rem" color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    disabled={sendLoading}
                  >
                    {sendLoading ? "Enviando..." : "Enviar Relatório por E-mail"}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </PageContainer>
  );
};

export default EFRelatorioFinanceiroPage;
